export const PENDING_TEAM_CODE_KEY = 'charlie-charlie.pending-team-code';
export const PENDING_TEAM_CODE_TTL_MS = 10 * 60 * 1000;
const cancelled = () => new Error('Google sign-in was cancelled. Please try again.');

// A code belongs to one sign-in attempt in one tab. Bind it only to the account
// returned by that attempt, then remove it before making the server request.
export function createPendingTeamCodeStore({storage,now=Date.now,createId=()=>crypto.randomUUID()}={}) {
  const waiting = new Map();
  function inspect() {
    let raw,record;
    try { raw=storage?.getItem(PENDING_TEAM_CODE_KEY); record=JSON.parse(raw||'null'); }
    catch { return {raw:raw||null,record:null,...(raw?{error:cancelled()}:{})}; }
    if (!raw) return {raw:null,record:null};
    const age=now()-record?.createdAt;
    const valid=record&&/^\d{6}$/.test(record.code)&&typeof record.flowId==='string'&&['popup','redirect'].includes(record.mode)&&Number.isFinite(age)&&age>=0&&age<PENDING_TEAM_CODE_TTL_MS&&(record.uid===null||typeof record.uid==='string');
    return {raw,record,error:valid?null:new Error('Your sign-in attempt expired. Enter your teacher’s code and sign in again.')};
  }
  function read() {
    const state=inspect();
    if (state.error) { clear(); return null; }
    return state.record;
  }
  function clear(flowId) {
    if (flowId && inspect().record?.flowId!==flowId) return;
    try { storage?.removeItem(PENDING_TEAM_CODE_KEY); } catch {}
    for (const wake of waiting.values()) wake();
    waiting.clear();
  }
  function write(record) {
    try {
      if (!storage) throw new Error('No session storage');
      storage.setItem(PENDING_TEAM_CODE_KEY,JSON.stringify(record));
    } catch { throw new Error('Your browser could not keep the team code. Allow website storage and try again.'); }
  }
  return {
    read,clear,inspect,
    begin(code,mode) {
      clear();
      if (!/^\d{6}$/.test(code)) throw new Error('Enter the 6-digit code from your teacher before signing in.');
      const record={flowId:createId(),code,mode,createdAt:now(),uid:null};
      write(record);
      if (mode==='popup') {
        let wake; const promise=new Promise(resolve=>{wake=resolve;});
        waiting.set(record.flowId,Object.assign(wake,{promise}));
      }
      return record.flowId;
    },
    bind(uid,flowId) {
      const record=read();
      if (!record || (flowId&&record.flowId!==flowId)) return false;
      write({...record,uid});
      const wake=waiting.get(record.flowId);waiting.delete(record.flowId);wake?.();
      return true;
    },
    async forUser(uid) {
      let record=read();
      if (!record) return null;
      const flowId=record.flowId;
      if (record.uid===null) {
        const wake=waiting.get(flowId);
        // A stale popup cannot resume after a reload; redirect binding is done
        // by getRedirectResult before account enrolment starts.
        if (!wake) { clear(flowId); return null; }
        await wake.promise;
        record=read();
      }
      if (!record||record.flowId!==flowId||record.uid!==uid) throw cancelled();
      return record;
    },
    take(uid,flowId) {
      const record=read();
      if (!record||record.uid!==uid||record.flowId!==flowId) throw cancelled();
      clear(flowId);
      return record.code;
    },
  };
}

// Existing accounts may recover from a redirect lookup failure only when no
// explicit code-first attempt is pending. Otherwise stop before old membership
// can become visible, including expired attempts and a missing redirect result.
export async function recoverGoogleTeamRedirect({pending,resolveRedirect,currentUser,signOut,onFailure=()=>{},isAdmin=false}) {
  if (isAdmin) pending.clear();
  const attempt=pending.inspect();
  let owned=false;
  try {
    const result=await resolveRedirect();
    if (!attempt.raw) return result;
    const current=pending.inspect();
    if (current.raw!==attempt.raw) throw cancelled();
    owned=true;
    if (attempt.error||current.error) throw attempt.error||current.error;
    if (attempt.record.mode!=='redirect'||!result?.user) throw new Error('Google sign-in could not be completed. Enter your teacher’s code and sign in again.');
    if (!pending.bind(result.user.uid,attempt.record.flowId)) throw cancelled();
    return result;
  } catch (error) {
    const current=pending.inspect();
    if (attempt.raw&&(current.raw===attempt.raw||(owned&&!current.raw))) {
      pending.clear();
      onFailure(error);
      const user=currentUser();
      if (user) try { await signOut(user); } catch {}
    }
    throw error;
  }
}

export async function prepareGoogleTeamAccount({user,currentUser,pending,enrol,join,refresh,isCompany=false,signOut,onFailure=()=>{}}) {
  const started=pending.inspect();
  let attempt=started.record;
  const active=()=>currentUser()?.uid===user.uid;
  try {
    if (started.error) throw started.error;
    attempt=await pending.forUser(user.uid);
    if (!active()) throw cancelled();
    const registered=await enrol();
    if (!active()) throw cancelled();
    // Also cover an Auth observation that began just before a new popup.
    attempt=await pending.forUser(user.uid)||attempt;
    let profile=registered.profile;
    if (attempt) {
      if (isCompany) throw new Error('Use a student Google / Gmail account for this team code.');
      if (!active()) throw cancelled();
      const accessCode=pending.take(user.uid,attempt.flowId);
      profile=(await join(accessCode)).profile;
      if (!active()) throw cancelled();
    }
    await refresh();
    if (!active()) throw cancelled();
    return profile;
  } catch (error) {
    const current=pending.inspect().record;
    const ownsAttempt=(attempt||started.raw)&&(!current||(current.flowId===attempt?.flowId&&(!current.uid||current.uid===user.uid)));
    if (ownsAttempt) {
      pending.clear(attempt?.flowId);
      // Do not expose a returning student's old team after the requested code
      // failed. A different account's newer sign-in must remain untouched.
      if (active()) { onFailure(error); try { await signOut(); } catch {} }
    }
    throw error;
  }
}
